import { useState } from 'react'
import { Heart, Repeat2, MessageCircle, Quote, Bookmark, Share } from 'lucide-react'
import { strings } from '@/strings'

export interface PostEngagementBarProps {
    /** Icon size. sm=w-4 h-4, lg=w-5 h-5. Default: 'sm'. */
    size?: 'sm' | 'lg'
    /** Layout mode. split=left/right groups with justify-between, spread=justify-around. Default: 'split'. */
    layout?: 'split' | 'spread'
    /** Grays all interactive buttons (opacity-40, cursor-not-allowed, pointer-events-none). Does not affect share. */
    disabled?: boolean
    /** Changes repost dropdown label to Boost/Undo boost. */
    isActivityPub?: boolean

    /** Reply count. Absent = no reply element. */
    replyCount?: number
    /** Present = interactive button. Absent with replyCount = read-only div. */
    onReply?: () => void

    /** Repost count. */
    repostCount?: number
    /** Whether the current user has reposted. */
    isReposted?: boolean
    /** Absent = no repost element. When canQuote+onQuote: opens dropdown. Otherwise: direct action. */
    onRepost?: () => void
    /** Quote action for dropdown. Only shown when canQuote is true. */
    onQuote?: () => void
    /** Whether to show the Quote option in the dropdown. */
    canQuote?: boolean

    /** Like count. Absent = no like element. */
    likeCount?: number
    /** Whether the current user has liked. */
    isLiked?: boolean
    /** Absent = no like element. */
    onLike?: () => void

    /** Absent = no share button. Share is never disabled. */
    onShare?: () => void

    /** Whether the post is bookmarked. */
    isBookmarked?: boolean
    /** Absent = no bookmark button. */
    onBookmark?: () => void
}

export function PostEngagementBar({
    size = 'sm',
    layout = 'split',
    disabled = false,
    isActivityPub = false,
    replyCount,
    onReply,
    repostCount,
    isReposted = false,
    onRepost,
    onQuote,
    canQuote = false,
    likeCount,
    isLiked = false,
    onLike,
    onShare,
    isBookmarked = false,
    onBookmark,
}: PostEngagementBarProps) {
    const [showRepostMenu, setShowRepostMenu] = useState(false)

    const showQuoteOption = !!onQuote && !!canQuote

    const handleRepostClick = () => {
        if (showQuoteOption) {
            setShowRepostMenu(prev => !prev)
        } else {
            onRepost?.()
        }
    }

    const iconClass = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'
    const gapClass = size === 'lg' ? 'gap-2' : 'gap-1.5'
    const countClass = size === 'lg' ? 'text-sm' : 'text-xs'
    const disabledClass = disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''
    const menuPositionClass =
        layout === 'spread'
            ? 'absolute left-1/2 -translate-x-1/2 bottom-10'
            : 'absolute left-0 bottom-8'

    const repostLabel = isActivityPub
        ? isReposted
            ? strings.posts.undoBoost
            : strings.posts.boost
        : isReposted
          ? strings.posts.undoRepost
          : strings.posts.repost

    const replyElement =
        replyCount !== undefined ? (
            onReply ? (
                <button
                    onClick={e => {
                        e.stopPropagation()
                        onReply()
                    }}
                    disabled={disabled}
                    aria-label={strings.posts.reply}
                    className={`flex items-center ${gapClass} text-text-secondary-light dark:text-text-secondary-dark hover:text-primary transition-colors ${disabledClass}`}
                >
                    <MessageCircle className={iconClass} />
                    {replyCount > 0 && <span className={countClass}>{replyCount}</span>}
                </button>
            ) : (
                <div
                    className={`flex items-center ${gapClass} text-text-secondary-light dark:text-text-secondary-dark`}
                >
                    <MessageCircle className={iconClass} />
                    {replyCount > 0 && <span className={countClass}>{replyCount}</span>}
                </div>
            )
        ) : null

    const repostElement = onRepost ? (
        <div className="relative">
            <button
                aria-label={strings.posts.repostOptions}
                onClick={e => {
                    e.stopPropagation()
                    handleRepostClick()
                }}
                disabled={disabled}
                className={`flex items-center ${gapClass} transition-colors ${
                    isReposted
                        ? 'text-green-500'
                        : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-green-500'
                } ${disabledClass}`}
            >
                <Repeat2 className={iconClass} />
                {(repostCount ?? 0) > 0 && <span className={countClass}>{repostCount}</span>}
            </button>
            {showRepostMenu && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={e => {
                            e.stopPropagation()
                            setShowRepostMenu(false)
                        }}
                    />
                    <div
                        className={`${menuPositionClass} z-20 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg py-1 min-w-[140px]`}
                    >
                        <button
                            onClick={e => {
                                e.stopPropagation()
                                setShowRepostMenu(false)
                                onRepost()
                            }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-surface-light dark:hover:bg-surface-dark/80"
                        >
                            <Repeat2 className="w-4 h-4" />
                            {repostLabel}
                        </button>
                        {canQuote && onQuote && (
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    setShowRepostMenu(false)
                                    onQuote()
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-text-primary-light dark:text-text-primary-dark hover:bg-surface-light dark:hover:bg-surface-dark/80"
                            >
                                <Quote className="w-4 h-4" />
                                {strings.posts.quote}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    ) : null

    const likeElement = onLike ? (
        <button
            onClick={e => {
                e.stopPropagation()
                onLike()
            }}
            disabled={disabled}
            className={`flex items-center ${gapClass} transition-colors ${
                isLiked
                    ? 'text-red-500'
                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-red-500'
            } ${disabledClass}`}
        >
            <Heart className={iconClass} fill={isLiked ? 'currentColor' : 'none'} />
            {(likeCount ?? 0) > 0 && <span className={countClass}>{likeCount}</span>}
        </button>
    ) : null

    const shareElement = onShare ? (
        <button
            onClick={e => {
                e.stopPropagation()
                onShare()
            }}
            className="flex items-center text-text-secondary-light dark:text-text-secondary-dark hover:text-primary transition-colors"
            aria-label={strings.posts.sharePost}
        >
            <Share className={iconClass} />
        </button>
    ) : null

    const bookmarkElement = onBookmark ? (
        <button
            onClick={e => {
                e.stopPropagation()
                onBookmark()
            }}
            disabled={disabled}
            className={`flex items-center ${gapClass} transition-colors ${
                isBookmarked
                    ? 'text-accent-bluesky'
                    : 'text-text-secondary-light dark:text-text-secondary-dark hover:text-accent-bluesky'
            } ${disabledClass}`}
            aria-label={isBookmarked ? strings.posts.removeBookmark : strings.posts.bookmark}
        >
            <Bookmark className={iconClass} fill={isBookmarked ? 'currentColor' : 'none'} />
        </button>
    ) : null

    if (layout === 'spread') {
        return (
            <div className="flex items-center justify-around" onClick={e => e.stopPropagation()}>
                {replyElement}
                {repostElement}
                {likeElement}
                {bookmarkElement}
                {shareElement}
            </div>
        )
    }

    return (
        <div className="flex items-center justify-between" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-6">
                {replyElement}
                {repostElement}
                {likeElement}
            </div>
            {(shareElement ?? bookmarkElement) && (
                <div className="flex items-center gap-3">
                    {shareElement}
                    {bookmarkElement}
                </div>
            )}
        </div>
    )
}
